// On-site (live) event engraving. Facts come from the owner's 2026 event quotes
// and the event app spec (laser-engraving-event-app-tag-design, docs/SPEC.md).
// No event has run yet: describe the setup and process, never past results,
// attendance numbers, or client names. See .seo/briefs/on-site-laser-engraving-nyc.md.
const { images } = require('./engraving-services')

const facts = {
  path: '/services/on-site-laser-engraving-nyc/',
  reviewedDate: '2026-10-28',
  machine: 'xTool F2',
  bed: '115 × 115 mm',
  engravingArea: 'about 4.5 inches',
  piecesPerHour: '40–50',
  setupTime: 'about 30 minutes',
  bookingLead: 'four or more weeks',
  deposit: '50%',
}

// OWNER TO CONFIRM before publishing. The figure comes from one recent
// single-station NYC evening quote; the owner has not approved showing it.
// Set `published: true` only after they do. While false, no dollar amount
// renders anywhere on the page or in its structured data.
const startingPrice = {
  published: false,
  amount: 5500,
  display: 'From $5,500',
  basis: 'One station for one evening in New York City, plus tax.',
}

const page = {
  path: facts.path,
  title: 'On-Site & Live Laser Engraving in NYC | PrintAWorld',
  description:
    'Live laser engraving stations for NYC events, trade shows, and brand activations. Guests personalize tags or leather from their phones.',
  eyebrow: 'On-site laser engraving / New York City',
  heading: 'On-site laser engraving for NYC events and brand activations.',
  answer:
    'PrintAWorld brings a live laser engraving station to your New York event. Guests scan a QR code at the booth, type a name, choose an approved font or icon, and follow their claim number on their phone while we engrave the piece on site. Each station is a portable, enclosed laser run by an engraver and an assistant, set up in about 30 minutes at a table-sized spot with a standard outlet.',
  cta: 'Get an event engraving quote',
  hero: 'leather',
}

const keyFacts = [
  {
    label: 'Per station',
    value: '40–50 / hour',
    note: 'Names on small items · logos add time',
  },
  {
    label: 'Engraving area',
    value: 'Up to ~4.5″',
    note: '115 × 115 mm bed · flat items',
  },
  { label: 'Setup', value: '~30 minutes', note: 'One 120V outlet per station' },
  { label: 'Staffing', value: '2 people', note: 'Engraver + assistant' },
]

const guestSteps = [
  {
    title: 'Scan the booth QR',
    body: 'Guests scan the live code on the booth screen. It changes every few minutes, so a screenshot shared outside the venue stops working.',
  },
  {
    title: 'Type a name, pick a style',
    body: 'Guests type a name and choose from the fonts and icons you approved. Characters a font cannot show, names that will not fit, and obvious profanity are caught before submitting.',
  },
  {
    title: 'Check the live preview',
    body: 'The preview updates as they type, showing the layout on the item. Guests see what will be engraved before they submit.',
  },
  {
    title: 'Get a claim number',
    body: 'Each guest gets a claim number and a status page showing their place in the queue and an estimated wait, so they can keep exploring the event.',
  },
  {
    title: 'Engraved on site',
    body: 'Our engraver runs pieces from the queue on the enclosed laser while the assistant handles pickup. Typos can be corrected at the station before a piece is engraved.',
  },
  {
    title: 'Collect when ready',
    body: 'The status page flips to ready. A ready board at the booth lists finished claim numbers for anyone who closed the tab.',
  },
]

const items = [
  {
    label: '01 / SMALL & FLAT',
    title: 'Tags, cards, and keychains.',
    body: `Luggage tags, dog tags, leather cardholders, keychains, metal cards, and coasters suit a live station. The engraving area is up to ${facts.engravingArea} (a ${facts.bed} bed), and the item needs to sit flat inside the enclosed laser.`,
  },
  {
    label: '02 / YOUR ITEMS',
    title: 'Supply the items, plus spares.',
    body: 'Clients usually supply the items, with spares for testing. We can source leather tags or cardholders on request. Before the event, we test on your actual item and confirm the look and time per piece.',
  },
  {
    label: '03 / BETTER AT THE STUDIO',
    title: 'Bottles and larger pieces.',
    body: 'Bottles and large or curved items are better engraved ahead of time at our New York studio than at a live station. Tell us what you plan to hand out, and we will suggest the right route.',
  },
]

const capacityRows = [
  { label: 'Name on a small, flat item', value: 'Roughly 40–50 per hour' },
  { label: 'Logo or icon added', value: 'Adds time per piece' },
  { label: 'Second station', value: 'Shares one guest queue' },
  { label: 'Final rate', value: 'Confirmed after testing your item' },
]

const venueNeeds = [
  {
    title: 'A table-sized spot per station',
    body: 'We can bring the table and chair.',
  },
  {
    title: 'A standard 120V outlet per station',
    body: 'One regular wall outlet or power drop for each laser.',
  },
  {
    title: 'About 30 minutes to set up',
    body: 'Before guests arrive, inside your load-in window.',
  },
  {
    title: 'Internet for the guest app',
    body: 'We can bring our own hotspot if venue Wi-Fi is unreliable.',
  },
  {
    title: 'Your insurance requirements',
    body: 'Tell us what the venue or agency requires, such as a certificate of insurance (COI). We confirm it in the quote.',
  },
]

const bookingSteps = [
  {
    title: 'Send the brief',
    body: 'Event date and hours, venue, expected guest count, the item and quantity, and the fonts, icons, or logo guests can choose from.',
  },
  {
    title: 'Test on your item',
    body: 'We engrave your actual item, ideally on spares, to confirm the settings, the look, and the time per piece. You approve the sample.',
  },
  {
    title: 'Reserve the date',
    body: `A ${facts.deposit} deposit reserves the date once you approve the sample. The balance is due before the event.`,
  },
]

const priceFactors = [
  { label: 'Stations', value: 'One, or two sharing one queue' },
  { label: 'Event hours', value: 'Engraving time plus setup' },
  { label: 'Items', value: 'Client-supplied or sourced by us' },
  { label: 'Artwork', value: 'Names only, or names plus icons or logos' },
]

const priceAnswer = startingPrice.published
  ? `On-site engraving is quoted per event, in writing. A recent single-station NYC activation was quoted from $${startingPrice.amount.toLocaleString('en-US')} for one evening. The number of stations, event hours, items, and artwork affect the quote. Prices assume an event in New York City, plus tax.`
  : 'On-site engraving is quoted per event, in writing. The number of stations, event hours, items, and artwork affect the quote. Prices assume an event in New York City, plus tax. Send the date, venue, and item for a written quote.'

const faqs = [
  {
    question: 'What is on-site laser engraving?',
    answer:
      'On-site, or live, laser engraving means the engraving happens at your event. PrintAWorld sets up a portable, enclosed laser station staffed by an engraver and an assistant. Guests personalize their piece from their phone and collect it when their claim number is ready.',
  },
  {
    question: 'How many pieces can a live engraving station do per hour?',
    answer:
      'Roughly 40–50 pieces per hour per station for a name on a small item. Adding a logo or icon takes longer. We confirm the rate after testing your actual item. A second station can share the same guest queue.',
  },
  {
    question: 'What items work best for live event engraving?',
    answer: `Small, flat items with an engraving area up to ${facts.engravingArea}: luggage tags, dog tags, leather cardholders, keychains, metal cards, and coasters. Bottles and large or curved items are better engraved ahead of time at our studio.`,
  },
  {
    question: 'What does the venue need to provide?',
    answer:
      'A table-sized spot and a standard 120V outlet for each station, plus about 30 minutes to set up. We can bring the table, chair, and our own internet hotspot.',
  },
  {
    question: 'Do guests need to download an app or create an account?',
    answer:
      'No. Guests scan the booth QR code and use a web page in their phone’s browser. There is no login, and the guest app does not collect email addresses.',
  },
  {
    question: 'How far ahead should we book on-site engraving in NYC?',
    answer: `Four or more weeks ahead is comfortable. We start with your brief, test on your item, and reserve the date with a ${facts.deposit} deposit after you approve the sample. The balance is due before the event.`,
  },
  {
    question: 'How much does on-site laser engraving cost?',
    answer: priceAnswer,
  },
  {
    question: 'Is live laser engraving the same as hand engraving?',
    answer:
      'No. A hand engraver or calligrapher letters each piece by hand. Our station uses an enclosed laser with the fonts and icons you approve, so every piece follows the same brand layout. For hand lettering on bottles or glassware, a hand engraver is the better fit.',
  },
  {
    question: 'Can you provide a certificate of insurance for the venue?',
    answer:
      'Send the venue’s or agency’s insurance requirements, such as a certificate of insurance (COI), with your brief. We confirm what we can provide in the written quote.',
  },
]

const quoteChecklist = [
  'Event date, hours, and venue address',
  'Expected guest count',
  'The item, quantity, and spares for testing, or ask us to source leather',
  'Fonts, icons, or logo artwork guests can choose from',
  'Venue or agency requirements, such as a COI',
]

module.exports = {
  onsiteEngravingFacts: facts,
  startingPrice,
  page,
  images,
  keyFacts,
  guestSteps,
  items,
  capacityRows,
  venueNeeds,
  bookingSteps,
  priceFactors,
  faqs,
  quoteChecklist,
}
